import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import CricketScoreSummary from '../components/cricket/CricketScoreSummary';
import CricketScoreSequence from '../components/cricket/CricketScoreSequence';
import TtScoreSummary from '../components/tabletennis/TtScoreSummary';
import TtScoreSequence from '../components/tabletennis/TtScoreSequence';
import TennisScoreSummary from '../components/tennis/TennisScoreSummary';
import TennisScoreSequence from '../components/tennis/TennisScoreSequence';
import {
  Box,
  Button,
  Center,
  Flex,
  Grid,
  Heading,
  Spinner,
  Text,
  useBreakpointValue,
  VStack,
  Badge,
  Link,
  Container,
  IconButton,
  Collapse
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { 
  Market, 
  MarketType} from '../types/marketTypes';
import { EventStatus } from '../types/types';
import { formatDateTime, getStatusColor, sportOf } from '../utils/eventUtils';
import { tennisMarketSections, ttCurrentSet, ttMarketSections } from '../utils/marketUtils';

// Import our custom hooks
import { useEventDetails } from '../hooks/useEventDetails';

// Import our reusable components
import { VideoPlayer } from '../components/events/VideoPlayer';
import { RecordingPanel } from '../components/events/RecordingPanel';
import { TradingMarketSection } from '../components/events/TradingMarketSection';
import { TradingPasswordProtection } from '../components/TradingPasswordProtection';
import RuleSetBadge from '../components/common/RuleSetBadge';

const TradingEventDetailsContent: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const eventId = id ? parseInt(id, 10) : 0;
  const navigate = useNavigate();
  const [expandedInnings, setExpandedInnings] = useState<Record<string, boolean>>({});
  
  // Toggle inning expansion
  const toggleInning = (inning: string) => {
    setExpandedInnings(prev => ({
      ...prev,
      [inning]: !prev[inning]
    }));
  };
  
  // Responsive values
  const buttonSize = useBreakpointValue({ base: 'xs', md: 'sm' });
  const headingSize = useBreakpointValue({ base: 'sm', md: 'md' });
  const isMobile = useBreakpointValue({ base: true, md: false });
  
  // Use our custom hook to manage event details and markets (read-only)
  const {
    event,
    markets,
    isLoading,
    isMarketsLoading
  } = useEventDetails(eventId);
  
  // Debug event and cricket scores
  React.useEffect(() => {
    if (event) {
      console.group('TradingEventDetailsPage - Event Debug');
      console.log('Event:', event);
      console.log('Sport name:', event.sport?.name);
      console.log('Cricket scores available:', event.cricketScores ? 'Yes' : 'No');
      if (event.cricketScores) {
        console.log('Number of cricket scores:', event.cricketScores.length);
      }
      console.groupEnd();
    }
  }, [event]);

  // Group markets by their marketType for display
  const groupedMarkets = markets.reduce((acc, market) => {
    (acc[market.marketType] = acc[market.marketType] || []).push(market);
    return acc;
  }, {} as Record<string, Market[]>);

  const areAllMarketsSettled = () => {
    if (markets.length === 0) return false;
    return markets.every(market => market.marketOutcome === 'FINISHED');
  };

  const areAllMarketsVoided = () => {
    if (markets.length === 0) return false;
    return markets.every(market => market.marketOutcome === 'VOIDED');
  };

  const shouldShowVideo = () => {
    if (!event?.videoUrl || event.videoUrl.trim() === '' || event.status !== EventStatus.ACTIVE) {
      return false;
    }
    if (markets.length === 0) return true;
    return !areAllMarketsSettled() && !areAllMarketsVoided();
  };

  const getHeaderBackgroundColor = () => {
    if (markets.length === 0) return 'gray.50';
    
    const hasFinished = markets.some(market => market.marketOutcome === 'FINISHED');
    const allFinishedOrVoided = markets.every(market => 
      market.marketOutcome === 'FINISHED' || market.marketOutcome === 'VOIDED'
    );
    
    if (hasFinished && allFinishedOrVoided) return 'green.50';
    if (areAllMarketsSettled()) return 'green.50';
    if (areAllMarketsVoided()) return 'red.50';
    return 'gray.50';
  };
  
  // Sort Inning Runs markets: First Inning first
  if (groupedMarkets[MarketType.INNING_RUNS]) {
    groupedMarkets[MarketType.INNING_RUNS].sort((a, b) => {
      const aRef = a.reference || '';
      const bRef = b.reference || '';
      
      if (aRef === 'FIRST_INNING') return -1;
      if (bRef === 'FIRST_INNING') return 1;
      if (aRef === 'SECOND_INNING') return -1;
      if (bRef === 'SECOND_INNING') return 1;
      
      return 0;
    });
  }
  
  // Sort Team Boundaries: 4s then 6s
  const sortBoundaries = (markets: Market[]) => {
    return markets.sort((a, b) => {
      if (a.marketType === b.marketType) return 0;
      if (a.marketType === MarketType.TEAM_4S || a.marketType === MarketType.TOTAL_4S) return -1;
      return 1;
    });
  };
  
  if (groupedMarkets[MarketType.TEAM_4S] || groupedMarkets[MarketType.TEAM_6S]) {
    const teamBoundaries = [
      ...(groupedMarkets[MarketType.TEAM_4S] || []),
      ...(groupedMarkets[MarketType.TEAM_6S] || [])
    ];
    sortBoundaries(teamBoundaries);
  }
  
  if (groupedMarkets[MarketType.TOTAL_4S] || groupedMarkets[MarketType.TOTAL_6S]) {
    const matchBoundaries = [
      ...(groupedMarkets[MarketType.TOTAL_4S] || []),
      ...(groupedMarkets[MarketType.TOTAL_6S] || [])
    ];
    sortBoundaries(matchBoundaries);
  }
  
  // Parse reference values helper function
  const parseReference = (ref: string | undefined): [number, number, number] => {
    if (!ref) return [0, 0, 0];
    
    const parts = ref.split(':');
    const inning = parseInt(parts[0]) || 0;
    const over = parseInt(parts[1]) || 0;
    const ball = parseInt(parts[2]) || 0;
    
    return [inning, over, ball];
  };
  
  // Sort and group Next Ball markets by inning number from reference field
  let nextBallMarketsByInning: Record<number, Market[]> = {};
  
  if (groupedMarkets[MarketType.NEXT_BALL]) {
    // First sort all Next Ball markets
    groupedMarkets[MarketType.NEXT_BALL].sort((a, b) => {
      const [aInning, aOver, aBall] = parseReference(a.reference);
      const [bInning, bOver, bBall] = parseReference(b.reference);
      
      // Sort by inning first, then over, then ball
      if (aInning !== bInning) return aInning - bInning;
      if (aOver !== bOver) return aOver - bOver;
      return aBall - bBall;
    });
    
    // Then group by inning
    nextBallMarketsByInning = groupedMarkets[MarketType.NEXT_BALL].reduce((acc, market) => {
      const [inning] = parseReference(market.reference);
      (acc[inning] = acc[inning] || []).push(market);
      return acc;
    }, {} as Record<number, Market[]>);
  }
  
  // Use a ref to track the previous innings keys to avoid unnecessary re-renders
  const prevInningsKeysRef = useRef<string>('');
  
  // Initialize expanded innings state when markets change
  useEffect(() => {
    // Only run this effect when markets are loaded
    if (!isMarketsLoading && markets.length > 0) {
      // Create a string key representing the current set of innings
      const inningKeysString = Object.keys(nextBallMarketsByInning).sort().join(',');
      
      // Only update if we have innings and they've changed from previous render
      if (inningKeysString && inningKeysString !== prevInningsKeysRef.current) {
        // By default, expand the latest inning and collapse others
        const innings = Object.keys(nextBallMarketsByInning).sort((a, b) => parseInt(b) - parseInt(a));
        const newExpandedState: Record<string, boolean> = {};
        
        // Set all innings to expanded by default
        innings.forEach((inning) => {
          newExpandedState[inning] = true; // All innings expanded
        });
        
        setExpandedInnings(newExpandedState);
        prevInningsKeysRef.current = inningKeysString;
      }
    }
  }, [isMarketsLoading, markets.length]); // Only depend on markets loading state and length

  return (
    <Container maxW="container.xl" py={{ base: 3, md: 5 }} px={{ base: 2, md: 4 }}>
      {/* Back Button */}
      <Flex mb={{ base: 3, md: 5 }} justify="space-between" align="center" gap={2} flexWrap="wrap">
        <Button 
          leftIcon={<ChevronLeftIcon />} 
          onClick={() => navigate('/trading')} 
          variant="outline"
          size={buttonSize}
        >
          Back to Trading Dashboard
        </Button>
      </Flex>
      
      {/* Loading Spinner for Event Data */}
      {isLoading ? (
        <Center py={{ base: 6, md: 10 }}>
          <VStack spacing={{ base: 3, md: 4 }}>
            <Spinner size="xl" color="blue.500" thickness="4px" />
            <Text>Loading event details...</Text>
          </VStack>
        </Center>
      ) : (
        <>
          <Box mb={{ base: 4, md: 6 }} p={{ base: 3, md: 4 }} borderWidth="1px" borderRadius="lg" bg={getHeaderBackgroundColor()}>
            <Flex 
              direction={{ base: 'column', sm: 'row' }} 
              justify="space-between" 
              align={{ base: 'flex-start', sm: 'flex-start' }}
              gap={{ base: 3, sm: 3 }}
            >
              <VStack align="start" spacing={1} flex="1" minW={{ base: '100%', sm: '250px' }}>
                <Heading size={headingSize}>{event?.name ?? ''}</Heading>
                
                {/* Compact score in header, per sport */}
                {(() => {
                  const kind = sportOf(event);

                  if (kind === 'CRICKET' && event?.cricketScores && event.cricketScores.length > 0) {
                    return (
                      <Box mt={1}>
                        <CricketScoreSummary
                          cricketScores={event.cricketScores}
                          firstCompetitor={event.firstCompetitor}
                          secondCompetitor={event.secondCompetitor}
                          compact={true}
                        />
                      </Box>
                    );
                  }

                  if (kind === 'TENNIS' && event?.tennisScores && event.tennisScores.length > 0) {
                    return (
                      <Box mt={1}>
                        <TennisScoreSummary
                          tennisScores={event.tennisScores}
                          firstCompetitor={event.firstCompetitor}
                          secondCompetitor={event.secondCompetitor}
                          ruleSet={event.competition?.ruleSet}
                        />
                      </Box>
                    );
                  }

                  if (kind === 'TABLE_TENNIS' && event?.ttScores && event.ttScores.length > 0) {
                    return (
                      <Box mt={1}>
                        <TtScoreSummary
                          ttScores={event.ttScores}
                          firstCompetitor={event.firstCompetitor}
                          secondCompetitor={event.secondCompetitor}
                          compact={true}
                        />
                      </Box>
                    );
                  }
                  return null;
                })()}
                
                <Flex flexWrap="wrap" gap={2} align="center">
                  <Text color="gray.600" fontSize="sm">{event ? formatDateTime(event.startTime) : ''}</Text>
                  <Badge colorScheme={event ? getStatusColor(event.status) : 'gray'}>
                    {event ? EventStatus[event.status] : ''}
                  </Badge>
                </Flex>
                <Flex flexWrap="wrap" gap={2} align="center">
                  <Text fontSize="sm" color="gray.600">{event?.competition?.name ?? ''}</Text>
                  <Text fontSize="sm" color="gray.600" display={{ base: 'none', sm: 'inline' }}>•</Text>
                  <Text fontSize="sm" color="gray.600">{event?.sport?.name ?? ''}</Text>
                  <Text fontSize="sm" color="gray.600" display={{ base: 'none', sm: 'inline' }}>•</Text>
                  <RuleSetBadge ruleSet={event?.competition?.ruleSet} />
                </Flex>
                <Flex flexWrap="wrap" gap={2} align="center">
                  <Text fontSize="sm" color="gray.500">
                    ID: {sportOf(event) === 'TENNIS' ? event?.providerEventId : (
                      <>
                        {event?.providerEventId ? event.providerEventId.slice(0, -1) : ''}
                        <Text as="span" fontWeight="bold" color="blue.600">
                          {event?.providerEventId ? event.providerEventId.slice(-1) : ''}
                        </Text>
                      </>
                    )}
                  </Text>
                  <Text fontSize="sm" color="gray.500" display={{ base: 'none', sm: 'inline' }}>•</Text>
                  <Text fontSize="sm" color="gray.500">Provider: {event?.provider?.name ?? ''}</Text>
                </Flex>
              </VStack>
              
              {shouldShowVideo() && event?.videoUrl ? (
                <Box width={{ base: '100%', md: '300px' }}>
                  <Heading size="xs" mb={1}>Live Stream</Heading>
                  <VideoPlayer 
                    videoUrl={event.videoUrl} 
                    title={event?.firstCompetitor?.name && event?.secondCompetitor?.name ? 
                      `${event.firstCompetitor.name} vs ${event.secondCompetitor.name}` : 'Event Stream'}
                  />
                  <Link 
                    href={event.videoUrl} 
                    isExternal 
                    fontSize="xs" 
                    color="blue.500" 
                    mt={1} 
                    display="inline-block"
                  >
                    Open Stream
                  </Link>
                </Box>
              ) : (
                <RecordingPanel
                  matchId={event?.providerEventId}
                  startTime={event?.startTime}
                  title={event?.firstCompetitor?.name && event?.secondCompetitor?.name ?
                    `${event.firstCompetitor.name} vs ${event.secondCompetitor.name}` : 'Match recording'}
                />
              )}
            </Flex>
          </Box>

          {/* Markets Section */}
          {isMarketsLoading ? (
            <Center py={{ base: 6, md: 10 }}>
              <VStack spacing={{ base: 3, md: 4 }}>
                <Spinner size="xl" color="blue.500" thickness="4px" />
                <Text>Loading markets...</Text>
              </VStack>
            </Center>
          ) : (
            <>
              {/* Score sequence section, per sport */}
              {(() => {
                const kind = sportOf(event);

                if (kind === 'CRICKET' && event?.cricketScores && event.cricketScores.length > 0) {
                  return (
                    <Box mb={3} p={2} borderWidth="1px" borderRadius="lg" bg="gray.50">
                      <CricketScoreSequence cricketScores={event.cricketScores} />
                    </Box>
                  );
                }

                if (kind === 'TENNIS' && event?.tennisScores && event.tennisScores.length > 0) {
                  return (
                    <Box mb={3} p={2} borderWidth="1px" borderRadius="lg" bg="gray.50">
                      <TennisScoreSequence
                        tennisScores={event.tennisScores}
                        firstCompetitor={event.firstCompetitor}
                        secondCompetitor={event.secondCompetitor}
                        ruleSet={event.competition?.ruleSet}
                      />
                    </Box>
                  );
                }

                if (kind === 'TABLE_TENNIS' && event?.ttScores && event.ttScores.length > 0) {
                  return (
                    <Box mb={3} p={2} borderWidth="1px" borderRadius="lg" bg="gray.50">
                      <TtScoreSequence
                        ttScores={event.ttScores}
                        firstCompetitor={event.firstCompetitor}
                        secondCompetitor={event.secondCompetitor}
                      />
                    </Box>
                  );
                }
                return null;
              })()}
              
              {/* Table Tennis markets: the match-level picks, then everything each set settles */}
              {sportOf(event) === 'TABLE_TENNIS'
                && ttMarketSections(groupedMarkets, ttCurrentSet(event?.ttScores)).map(group => (
                <TradingMarketSection
                  key={group.title}
                  title={group.title}
                  markets={group.markets}
                  collapsible
                  defaultOpen={group.defaultOpen}
                  event={event ?? undefined}
                  compact={isMobile}
                />
              ))}

              {/* Tennis markets: the match-level picks, then the games of each set */}
              {sportOf(event) === 'TENNIS' && tennisMarketSections(groupedMarkets).map(group => (
                <TradingMarketSection
                  key={group.title}
                  title={group.title}
                  markets={group.markets}
                  event={event ?? undefined}
                  compact={isMobile}
                />
              ))}

              {/* Cricket markets layout */}
              {sportOf(event) === 'CRICKET' && (
              <>
              {/* First Row: Two Columns */}
              <Grid
                templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
                gap={3}
                mb={3}
              >
                {/* Left Column: Match Odds, Toss, and Boundaries */}
                <Box w="100%">
                  {/* Match Odds and Toss in horizontal layout */}
                  <Box mb={3}>
                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                      <Box>
                        <TradingMarketSection
                          title="Match Odds"
                          markets={groupedMarkets[MarketType.TWO_WAY_MATCH_ODDS] || groupedMarkets[MarketType.THREE_WAY_MATCH_ODDS] || []}
                          event={event ?? undefined}
                          compact={isMobile}
                        />
                      </Box>
                      <Box>
                        <TradingMarketSection
                          title="Toss"
                          markets={groupedMarkets[MarketType.TOSS] || []}
                          event={event ?? undefined}
                          compact={isMobile}
                        />
                      </Box>
                    </Grid>
                  </Box>

                  {/* Team Boundaries Section */}
                  <Box mb={3}>
                    <TradingMarketSection
                      title="Team Boundaries"
                      markets={[
                        ...(groupedMarkets[MarketType.TEAM_4S] || []),
                        ...(groupedMarkets[MarketType.TEAM_6S] || [])
                      ]}
                      event={event ?? undefined}
                    />
                  </Box>

                  {/* Match Boundaries Section */}
                  <Box>
                    <TradingMarketSection
                      title="Match Boundaries"
                      markets={[
                        ...(groupedMarkets[MarketType.TOTAL_4S] || []),
                        ...(groupedMarkets[MarketType.TOTAL_6S] || [])
                      ]}
                      event={event ?? undefined}
                    />
                  </Box>
                </Box>

                {/* Right Column: Innings Runs */}
                <Box w="100%">
                  <TradingMarketSection
                      title="Innings Runs"
                      markets={groupedMarkets[MarketType.INNING_RUNS] || []}
                      event={event ?? undefined}
                      gridColumns="repeat(2, 1fr)"
                  />

                  <Box>
                    {/* Total */}
                    <Box>
                      <TradingMarketSection
                          title="Other Totals"
                          markets={[
                            ...(groupedMarkets[MarketType.MATCH_TOTAL_RUNS] || []),
                            ...(groupedMarkets[MarketType.MATCH_TOTAL_WICKETS] || [])
                          ]}
                          event={event ?? undefined}
                      />
                    </Box>

                    {/* Odd/Even Section */}
                    <Box>
                      <TradingMarketSection
                          title="Odd/Even"
                          markets={[
                            ...(groupedMarkets[MarketType.TEAM_ODD_EVEN] || []),
                            ...(groupedMarkets[MarketType.MATCH_ODD_EVEN] || [])
                          ]}
                          event={event ?? undefined}
                      />
                    </Box>
                  </Box>
                </Box>
              </Grid>

              {/* Second Row: Next Ball Markets Grouped by Innings */}
              <Box mb={3}>
                <Heading size={headingSize} mb={2}>Next Ball</Heading>
                
                {/* If there are no Next Ball markets */}
                {Object.keys(nextBallMarketsByInning).length === 0 && (
                  <Box p={4} borderWidth="1px" borderRadius="lg" bg="gray.50">
                    <Text align="center" color="gray.500">No Next Ball markets available</Text>
                  </Box>
                )}
                
                {/* Display markets grouped by innings */}
                {Object.entries(nextBallMarketsByInning)
                  .sort(([inningA], [inningB]) => parseInt(inningA) - parseInt(inningB))
                  .map(([inning, markets]) => (
                    <Box key={`inning-${inning}`} mb={4}>
                      <Flex 
                        align="center" 
                        bg="gray.100" 
                        p={2} 
                        borderRadius="md"
                        mb={2}
                        cursor="pointer"
                        onClick={() => toggleInning(inning)}
                      >
                        <Heading size="xs" flex="1">Inning {inning}</Heading>
                        <IconButton
                          aria-label={expandedInnings[inning] ? "Collapse inning" : "Expand inning"}
                          icon={expandedInnings[inning] ? <ChevronUpIcon /> : <ChevronDownIcon />}
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleInning(inning);
                          }}
                        />
                      </Flex>
                      <Collapse in={expandedInnings[inning] ?? true} animateOpacity>
                        <Box pt={1} pb={2}>
                          <TradingMarketSection
                            title=""
                            markets={markets}
                            event={event ?? undefined}
                            compact={true}
                            gridColumns={isMobile ? 'repeat(auto-fill, minmax(120px, 1fr))' : undefined}
                          />
                        </Box>
                      </Collapse>
                    </Box>
                  ))
                }
              </Box>
              </>
              )}

              {/* Whatever this frontend has no mapping for: shown by its own name, never as another type */}
              {(groupedMarkets[MarketType.UNKNOWN] || []).length > 0 && (
                <TradingMarketSection
                  title="Unmapped markets"
                  markets={groupedMarkets[MarketType.UNKNOWN]}
                  event={event ?? undefined}
                  compact={isMobile}
                />
              )}
            </>
          )}
        </>
      )}
    </Container>
  );
};

export const TradingEventDetailsPage: React.FC = () => {
  return (
    <TradingPasswordProtection>
      <TradingEventDetailsContent />
    </TradingPasswordProtection>
  );
};
